import React, { type FC, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';
import { withImage } from '@lucid/hooks';

import reactHTMLParser from '../../utils/reactHTMLParser';
import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Heading from '../heading/heading';
import Link from '../link/link';
import Image from '../image/image';
import Text from '../text/text';
import CtaWithSSO from '../cta/cta-with-sso/cta-with-sso';

export type ImageOverflowType = 'vertical' | 'horizontal' | 'none' | null;
export type VerticalAlignmentType = 'top' | 'center' | 'bottom';

export interface MultipleImageHeroImageProps {
  /** The link to the image */
  imageUrl?: string;
  /** Allows for image overflow for select designs
   * * 'vertical': should use images with tall dimensions
   * * 'horizontal': should use images with wide dimensions
   * * 'none': meant for square images or with no intention of image overflow
   */
  allowImageOverflow?: ImageOverflowType;
  /** The vertical alignment of the image */
  imageVerticalAlignment?: VerticalAlignmentType;
  /** The offset of the vertical alignment */
  imageVerticalOffset?: number;
}

interface ImageContainerProps extends Pick<OptionalMultipleHeroProps, 'mobileImage' | 'shouldScaleImages'> {
  /** The name of the gridArea reference to assign to the container */
  gridArea: 'leftImage' | 'heroText' | 'rightImage';
  /** The image (right or left) being passed to the component */
  image: MultipleImageHeroImageProps;
  /** True if the Hero has a CTA element */
  hasCta?: boolean;
}

interface OptionalMultipleHeroProps {
  /** The variants for the MultipleImageHero component. */
  variant?: Lucid.Theme.Component.Hero.MultipleImage.VariantKeys;
  /** The variants for the cta button. */
  buttonVariant?: Lucid.Theme.Component.Button.VariantKeys;
  /** Translated string for aria label */
  ariaLabel?: string;
  /** Determines whether the page will add SSO buttons to CTA */
  hasSSO?: boolean;
  /** Which image to display on small screens */
  mobileImage?: 'leftImage' | 'rightImage';
  /** Allows to pass additional styles to the component */
  className?: string;
  /** Will use layout that scales images to fit in frame, rather than overflowing */
  shouldScaleImages?: boolean;
}

export type MultipleImageHeroProps = Omit<Lucid.Directus.Generated.Dual_Image_Hero, 'id'> & OptionalMultipleHeroProps;

const StyledMultipleImageHero = styled.div<OptionalMultipleHeroProps>(
  variant({
    scale: 'hero.multipleImage',
  }),
  {
    label: 'MultipleImageHero',
    WebkitFontSmoothing: 'antialiased',
  },
);

const ImageContainer: FC<PropsWithChildren<ImageContainerProps>> = ({
  image: { allowImageOverflow = 'none', imageUrl, imageVerticalAlignment = 'top', imageVerticalOffset = 0 },
  gridArea,
  mobileImage,
  shouldScaleImages,
  hasCta,
}) => (
  <Box
    css={theme => [
      {
        label: 'multipleImageHero',
        display: gridArea === mobileImage ? 'flex' : 'none',
        gridArea,
        margin: `${theme.space.s24}px auto`,
        position: 'relative',
        [theme.breakpoints.bp900.min]: {
          display: 'flex',
          margin: 0,
          maxWidth: 'initial',
          alignItems: 'center',
          justifyContent: gridArea === 'leftImage' ? 'flex-end' : 'flex-start',
        },
      },
      shouldScaleImages && {
        display: 'none',
        margin: 0,
        minHeight: '170px',
        [theme.breakpoints.bp768.min]: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: gridArea === 'leftImage' ? 'flex-end' : 'flex-start',
        },
      },
    ]}
  >
    <Image
      css={theme => [
        {
          maxWidth: '290px',
          [theme.breakpoints.bp900.min]: {
            maxWidth: '100%',
            position: 'absolute',
          },
        },
        allowImageOverflow === 'horizontal' && {
          [theme.breakpoints.bp900.min]: {
            minWidth: '150%',
          },
        },
        allowImageOverflow === 'none' && {
          maxHeight: '100%',
        },
        imageVerticalAlignment === 'top' && {
          top: imageVerticalOffset,
        },
        imageVerticalAlignment === 'bottom' && {
          bottom: imageVerticalOffset,
        },
        shouldScaleImages && {
          [theme.breakpoints.bp768.min]: {
            maxWidth: '290px',
            left: gridArea === 'rightImage' && hasCta ? '-20%' : 'unset',
            right: gridArea === 'leftImage' && hasCta ? '-20%' : 'unset',
            bottom: imageVerticalAlignment !== 'bottom' ? theme.space.s24 : 0,
            transform: hasCta ? 'scale(1.1) translateY(-5%)' : 'unset',
            position: 'absolute',
          },

          [theme.breakpoints.bp1220.min]: {
            left: 'unset',
            right: 'unset',
            bottom: imageVerticalAlignment !== 'bottom' ? 'unset' : imageVerticalOffset,
            transform: 'unset',
            maxWidth: '100%',
          },
        },
      ]}
      imageAlt=""
      imageSrc={imageUrl || ''}
      loading="eager"
    />
  </Box>
);

const MultipleImageHero: FC<PropsWithChildren<MultipleImageHeroProps>> = ({
  buttonVariant,
  className,
  mobileImage = 'rightImage',
  variant = 'dark',
  hasSSO,
  heading,
  text,
  shouldScaleImages,
  link_set,
  left_image,
  left_overflow,
  left_vertical_align,
  left_vertical_offset,
  right_image,
  right_overflow,
  right_vertical_align,
  right_vertical_offset,
  ...rest
}) => {
  const hasCta = hasSSO || !!link_set?.length;

  return (
    <StyledMultipleImageHero {...rest} className={className} variant={variant}>
      <Box css={{ overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
        <Box
          css={theme => [
            {
              display: 'grid',
              gap: theme.space.s24,
              gridTemplateAreas: `"${mobileImage}" "heroText"`,
              [theme.breakpoints.bp900.min]: {
                gridTemplateAreas: '"leftImage heroText rightImage"',
                gridTemplateColumns: '390px 680px 390px',
              },
            },
            shouldScaleImages && {
              paddingTop: theme.space.s48,
              gridTemplateAreas: '"heroText"',
              gridTemplateRows: 'auto',
              [theme.breakpoints.bp768.min]: {
                paddingTop: theme.space.s64,
                gap: theme.space.s4,
                gridTemplateAreas: hasCta
                  ? '". heroText ." ". heroText ." "leftImage heroText rightImage"'
                  : '". heroText . " "leftImage heroText rightImage"',
                gridTemplateRows: `auto auto ${hasCta ? '1fr' : ''}`,
                gridTemplateColumns: 'minmax(170px, 270px) minmax(500px, 680px) minmax(170px, 270px)',
              },
              [theme.breakpoints.bp1220.min]: {
                gap: theme.space.s24,
                gridTemplateAreas: '"leftImage heroText rightImage"',
                gridTemplateColumns: 'minmax(200px, 420px) minmax(500px, 680px) minmax(200px, 420px)',
                gridTemplateRows: 'auto',
              },
            },
          ]}
        >
          {left_image && (
            <ImageContainer
              gridArea="leftImage"
              hasCta={hasCta}
              image={{
                imageUrl: withImage(left_image),
                allowImageOverflow: left_overflow as ImageOverflowType,
                imageVerticalAlignment: left_vertical_align as VerticalAlignmentType,
                imageVerticalOffset: left_vertical_offset,
              }}
              mobileImage={mobileImage}
              shouldScaleImages={shouldScaleImages}
            />
          )}
          <Box
            css={theme => [
              {
                gridArea: 'heroText',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                padding: theme.space.s64,
                paddingTop: 0,
                [theme.breakpoints.bp900.min]: {
                  padding: `${theme.space.s80}px 0`,
                },
              },
              shouldScaleImages && {
                [theme.breakpoints.bp768.min]: {
                  display: 'grid',
                  gridTemplateRows: 'subgrid',
                  alignItems: 'start',
                  padding: `0 0 ${theme.space.s80}px`,
                },
                [theme.breakpoints.bp1220.min]: {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                },
              },
            ]}
          >
            <Heading as="h1" css={{ maxWidth: '655px' }} dataElementPageLocation="hero" variant="hero3XL700">
              {heading && Array.isArray(heading) ? reactHTMLParser(heading.join('<br/>')) : heading}
            </Heading>
            <Text dataElementPageLocation="hero" id="heroLinkDescription" variant="paragraphL400">
              {text}
            </Text>
            {hasSSO && (
              <CtaWithSSO
                linkText={link_set?.[0]?.links_id?.default_link_text}
                linkUrl={link_set?.[0]?.links_id?.final_url}
              />
            )}
            {!hasSSO &&
              link_set &&
              link_set.map(({ links_id }) => (
                <Link
                  key={`multiple-image-hero-link-${links_id?.id}`}
                  displayAsButton
                  aria-describedby="heroLinkDescription"
                  css={theme => ({
                    width: '100%',
                    [theme.breakpoints.bp500.min]: {
                      width: 'fit-content',
                      justifySelf: 'center',
                    },
                  })}
                  href={links_id?.final_url}
                  size="large"
                  variant={buttonVariant || 'primary'}
                >
                  {links_id?.default_link_text}
                </Link>
              ))}
          </Box>
          {right_image && (
            <ImageContainer
              gridArea="rightImage"
              hasCta={hasCta}
              image={{
                imageUrl: withImage(right_image),
                allowImageOverflow: right_overflow as ImageOverflowType,
                imageVerticalAlignment: right_vertical_align as VerticalAlignmentType,
                imageVerticalOffset: right_vertical_offset,
              }}
              mobileImage={mobileImage}
              shouldScaleImages={shouldScaleImages}
            />
          )}
        </Box>
      </Box>
    </StyledMultipleImageHero>
  );
};

export { MultipleImageHero };
export default MultipleImageHero;
